import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from '../../../constants/apiConfig';
import {
  selectGroupGuid,
  selectUserGuid,
} from '../../../slices/commonSelectors';
import { toast } from 'react-toastify';
import { ActionBase } from '../../../types/iapBaseTypes';
import { toUtcIso } from '../../../utils/dateTime';
import { ActionAssigneeKind, IapScope, ORGANIZATION } from '../ICSConstants';
import { useCfaDemoEnabled } from 'utils/featureFlags';
import {
  getHeleneDemoOrgActions,
  isHeleneDemoActionId,
  updateHeleneDemoOrgAction,
} from '../cfa-demo/heleneDemoOrgActions';
import type { ActionDependencyBlocker } from '../utils/actionDependencies';
import { orgEventKeys } from 'hooks/orgEvents/orgEventHooks';
import { actionErrorMessage } from './actionErrors';
import type { GeoLocation } from 'components/GeoLocationComponent/buildGeoLocationFromGeoJson';
import type { ActionTicketFieldValues } from '../actionTicket/actionTicketModel';

export type OrgActionBase = ActionBase &
  ActionTicketFieldValues & {
    id: UUID;
    action_id: UUID;
    display_id?: string | null;
    aor_id?: string | null;
    aor_name?: string | null;
    poc: string | null;
    briefed_poc: boolean;
    start_date: Date | null;
    status: string | null;
    target_date: Date | null;
    actual_date: Date | null;
    utc_offset?: number;
    parent_id?: UUID | null;
    priority?: string | null;
    assignee_kind?: ActionAssigneeKind | null;
    assignee_id?: string | null;
    geolocation?: GeoLocation | null;
  };

export type GetOrgActionResponse = OrgActionBase & {
  group_guid: UUID;
  user_guid: UUID;
  user_name?: string;
  poc_user_name?: string;
  version: number;
  timestamp: Date;
  index?: number;
  /** Every predecessor of this task, open or closed. */
  depends_on?: UUID[];
  /** The predecessors still open. Non-empty means this task is blocked. */
  blocked_by?: ActionDependencyBlocker[];
};

export type CreateOrgActionReqData = ActionBase &
  ActionTicketFieldValues & {
    user_guid?: UUID;
    aor_id?: string | null;
    poc?: string | null;
    briefed_poc?: boolean;
    start_date?: Date | null;
    status?: string | null;
    target_date?: Date | null;
    actual_date?: Date | null;
    parent_id?: UUID | null;
    utc_offset?: number;
    priority?: string | null;
    assignee_kind?: ActionAssigneeKind | null;
    assignee_id?: string | null;
    geolocation?: GeoLocation | null;
    /**
     * Predecessor `action_id`s. Omit to leave existing dependencies alone; send
     * an empty array to clear them.
     */
    depends_on?: UUID[];
  };

export type EditOrgActionRequest = CreateOrgActionReqData & {
  action_id: UUID;
  archived?: boolean;
};

export const keys = {
  base: ['org_actions'] as const,
  byGroupAndUser: (groupGuid: string, userId: string) =>
    [...keys.base, groupGuid, userId] as const,
} as const;

export const useOrgActions = (scope: IapScope = ORGANIZATION) => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const cfaEnabled = useCfaDemoEnabled();
  return useQuery({
    queryKey: [...keys.byGroupAndUser(group_guid, user_guid), cfaEnabled],
    queryFn: async () => {
      const resp = await axios.get<GetOrgActionResponse[]>(
        `${config.apiGateway.ics_org_actions}/${group_guid}`
      );
      const mapped = resp.data.map((a) => ({
        ...a,
        start_date: toUtcIso(
          a.start_date
        ) as GetOrgActionResponse['start_date'],
        target_date: toUtcIso(
          a.target_date
        ) as GetOrgActionResponse['target_date'],
        actual_date: toUtcIso(
          a.actual_date
        ) as GetOrgActionResponse['actual_date'],
        timestamp: toUtcIso(a.timestamp) as GetOrgActionResponse['timestamp'],
      }));
      if (!cfaEnabled || !group_guid || !user_guid) {
        return mapped;
      }
      const demos = getHeleneDemoOrgActions(group_guid, user_guid);
      const demoIds = new Set(demos.map((d) => String(d.action_id)));
      return [
        ...demos,
        ...mapped.filter((a) => !demoIds.has(String(a.action_id))),
      ];
    },
    enabled: scope === ORGANIZATION && !!group_guid,
  });
};

export const useCreateOrgAction = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateOrgActionReqData) => {
      return await axios.post<GetOrgActionResponse>(
        `${config.apiGateway.ics_org_actions}/${group_guid}`,
        { ...data, user_guid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.byGroupAndUser(group_guid, user_guid),
      });
    },
    onError: (error) => {
      toast.error(actionErrorMessage(error));
    },
  });
};

export const useUpdateOrgAction = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditOrgActionRequest) => {
      if (isHeleneDemoActionId(data.action_id)) {
        updateHeleneDemoOrgAction(data);
        return;
      }
      return await axios.put(
        `${config.apiGateway.ics_org_actions}/${group_guid}/${data.action_id}`,
        { ...data, user_guid, archived: data.archived ?? false }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.byGroupAndUser(group_guid, user_guid),
      });
      void queryClient.invalidateQueries({ queryKey: orgEventKeys.all });
    },
    onError: (error) => {
      toast.error(actionErrorMessage(error));
    },
  });
};
